import {
  detailToModel,
  FlatEnrichmentModel,
} from "@app/shared/mappers/enrichment-detail.adapter";
import {
  EnrichmentDetail,
  EnrichmentPriceItem,
} from "@app/shared/models/enrichment-detail.model";

export type SavedScope =
  | { readonly kind: "prices"; readonly prices: readonly EnrichmentPriceItem[] }
  | { readonly kind: "package"; readonly detail: EnrichmentDetail };

/**
 * Advance the discard snapshot by exactly what a save persisted, when the
 * whole current model is not a safe discard target: after a PARTIAL price
 * save, or after a save whose PATCH was in flight while the user kept
 * editing. The persisted values must advance (or a later Discard would
 * revert persisted prices on screen and a later save resend the stale
 * bodies restored from the snapshot), and nothing unsaved may enter.
 *
 * Pure — returns a fresh model rebuilt through `detailToModel` so every
 * legacy projection (`productOffering`, `productOfferingPrice`) stays
 * consistent with the merged `__detail`.
 */
export function advanceSnapshot(
  snapshot: FlatEnrichmentModel,
  saved: SavedScope,
): FlatEnrichmentModel {
  if (saved.kind === "prices") {
    return mergeSavedPricesIntoSnapshot(snapshot, saved.prices);
  }
  // The server's package fields, never its price list: a package save
  // persists no price, so the snapshot's prices stay what they were.
  const { prices: _serverPrices, ...packageFields } = saved.detail;
  return detailToModel({
    ...snapshot.__detail,
    ...packageFields,
    prices: snapshot.__detail.prices ?? [],
  });
}

/**
 * Overlay the server's copy of each saved price onto the snapshot; every
 * other price keeps the snapshot's value. A saved price the snapshot never
 * listed is appended so a Discard cannot vanish a persisted price.
 */
export function mergeSavedPricesIntoSnapshot(
  snapshot: FlatEnrichmentModel,
  savedPrices: readonly EnrichmentPriceItem[],
): FlatEnrichmentModel {
  const savedById = new Map(
    savedPrices.filter((p) => !!p.id).map((p) => [p.id, p]),
  );
  const prices = (snapshot.__detail.prices ?? []).map((p) => {
    const saved = savedById.get(p.id);
    if (!saved) return p;
    savedById.delete(p.id);
    return saved;
  });
  prices.push(...savedById.values());
  return detailToModel({ ...snapshot.__detail, prices });
}
